import Hls from 'hls.js';
import type {
  HlsAudioContext,
  AudioEventHandlers,
  StrategyController,
  BufferUpdateData,
} from './types';
import { trackError } from '../../../utils';

const BUFFER_THRESHOLD_SECONDS = 60;
const STATUS_POLL_INTERVAL_MS = 5000;
const END_RECOVERY_POLL_INTERVAL_MS = 1000;
const END_RECOVERY_MAX_WAIT_MS = 60_000;

/**
 * Initialize HLS streaming playback using HLS.js library.
 * Handles buffer checking and status polling internally.
 * Returns a controller for managing the HLS instance.
 */
export function initHlsAudio(
  ctx: HlsAudioContext,
  sourceUrl: string,
  startPosition: number,
  handlers: AudioEventHandlers
): StrategyController {
  const {
    audioRef,
    isPlayingRef,
    playbackRate,
    contentId,
    loadingTimeoutRef,
    callbacksRef,
    setIsLoading,
    setError,
  } = ctx;

  if (!audioRef.current) {
    return {
      setPlaybackRate: () => {},
      cleanup: () => {},
    };
  }

  // HLS instance (captured in closure)
  let hls: Hls | null = null;

  // Buffer state tracking
  let secondsGenerated = 0;
  let currentStatus = 'pending';
  let lastBufferCheckTime = 0;
  let statusPollInterval: ReturnType<typeof setInterval> | null = null;
  let isShuttingDown = false; // Prevent activity after switch to single-file

  const BUFFER_CHECK_COOLDOWN_MS = 5000; // 5 seconds between checks

  // Immediately stop HLS activity (called when switching to single-file)
  const stopHlsImmediately = () => {
    isShuttingDown = true;

    // Stop status polling immediately
    if (statusPollInterval) {
      clearInterval(statusPollInterval);
      statusPollInterval = null;
    }

    // Stop HLS loading immediately (don't destroy yet - let reinitialize handle that)
    if (hls) {
      hls.stopLoad();
    }
  };

  // Check buffer and trigger generation if needed
  // Server handles not double-triggering; we still poll to get status updates
  const checkBuffer = async (opts?: { force?: boolean }) => {
    if (isShuttingDown) return;

    const now = Date.now();
    if (!opts?.force && now - lastBufferCheckTime < BUFFER_CHECK_COOLDOWN_MS)
      return;
    lastBufferCheckTime = now;

    try {
      const res = await fetch('/api/buffer-check', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contentId,
          position: audioRef.current?.currentTime ?? 0,
        }),
      });

      if (res.status === 403 && !isShuttingDown) {
        const data = await res.json().catch(() => ({}));
        if (data?.requiresProPdf) {
          callbacksRef.current.onLimit?.('pdf');
        } else if (data?.requiresLogin) {
          callbacksRef.current.onLimit?.('signin');
        } else {
          callbacksRef.current.onLimit?.('upgrade');
        }
        stopHlsImmediately();
        return;
      }

      if (res.ok && !isShuttingDown) {
        const data = (await res.json()) as BufferUpdateData & {
          isSingleFile: boolean;
        };
        secondsGenerated = data.secondsGenerated;
        currentStatus = data.status;

        // Report update to parent (use ref to avoid stale closure)
        if (callbacksRef.current.onBufferUpdate) {
          callbacksRef.current.onBufferUpdate({
            secondsGenerated: data.secondsGenerated,
            secondsTotal: data.secondsTotal,
            status: data.status,
          });
        }

        // If content switched to single-file mode, trigger reinitialization
        if (data.isSingleFile && callbacksRef.current.onSwitchToSingleFile) {
          // Stop HLS immediately before triggering switch
          stopHlsImmediately();
          callbacksRef.current.onSwitchToSingleFile();
        }
      }
    } catch (err) {
      if (!isShuttingDown) {
        console.error('[Audio] Buffer check failed:', err);
      }
    }
  };

  /**
   * Reload the HLS stream with cache-bust to pick up newly generated segments.
   * This is a lighter operation than reinitialize() - same mode, just refresh manifest.
   * Used for recovery when playback hits end while content is still generating.
   */
  const reloadStream = (resumeTime: number) => {
    if (isShuttingDown || !audioRef.current) return;

    const cacheBustedUrl = `${sourceUrl}${sourceUrl.includes('?') ? '&' : '?'}_t=${Date.now()}`;

    // HLS.js path
    if (hls) {
      try {
        hls.destroy();
      } catch {
        // ignore
      }
      hls = null;

      if (Hls.isSupported()) {
        hls = new Hls({
          xhrSetup: (xhr) => {
            xhr.withCredentials = true;
          },
          startPosition: Math.max(0, resumeTime),
          manifestLoadingMaxRetry: 6,
        });
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
            loadingTimeoutRef.current = null;
          }
        });
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (isShuttingDown) return;

          if (
            data.response?.code === 410 ||
            (data.networkDetails as XMLHttpRequest)?.status === 410
          ) {
            console.log(
              '[Audio] Content completed (410), switching to single-file mode'
            );
            stopHlsImmediately();
            callbacksRef.current.onSwitchToSingleFile?.();
            return;
          }

          if (!data.fatal) {
            console.warn('[Audio] HLS non-fatal error:', data.details);
            return;
          }

          console.error('[Audio] HLS fatal error:', data);
          setIsLoading(false);
          setError('Failed to load audio');
          trackError('HLS fatal error (during reload)', contentId);
          callbacksRef.current.onError('Failed to load audio');
        });

        hls.loadSource(cacheBustedUrl);
        hls.attachMedia(audioRef.current);
        audioRef.current.playbackRate = playbackRate;
        return;
      }
    }

    // Native HLS fallback (iOS Safari)
    audioRef.current.src = cacheBustedUrl;
    audioRef.current.load();
    audioRef.current.playbackRate = playbackRate;
  };

  /**
   * Recover when playback hits the end but more content is being generated.
   * Uses reloadStream() for same-mode refresh (lighter than full reinitialize).
   * Note: reinitialize() in useAudioController handles mode switches (HLS → single-file).
   */
  const recoverFromEndIfGenerating = async () => {
    if (isShuttingDown || !audioRef.current) return;

    // Only recover when we're not actually done.
    if (currentStatus === 'completed') return;

    // Don't attempt recovery when page is hidden (iOS audio continues in background,
    // but network requests may block the audio stream).
    if (document.hidden) return;

    const resumeTime = audioRef.current.currentTime || 0;
    setIsLoading(true);

    // Force a buffer check to trigger generation if needed.
    await checkBuffer({ force: true });

    const startedAt = Date.now();
    let hasReloaded = false;

    while (
      !isShuttingDown &&
      Date.now() - startedAt < END_RECOVERY_MAX_WAIT_MS
    ) {
      // Stop polling if page becomes hidden during recovery.
      if (document.hidden) {
        setIsLoading(false);
        return;
      }

      // Force polling during recovery (ignore cooldown).
      await checkBuffer({ force: true });

      // If more audio has been generated beyond the point we ended, reload the stream.
      if (!hasReloaded && secondsGenerated > resumeTime + 0.5) {
        hasReloaded = true;
        reloadStream(resumeTime);
        break;
      }

      // If we switched to single-file mode, stop here (reinit handled by callback).
      if (isShuttingDown) return;

      await new Promise((r) => setTimeout(r, END_RECOVERY_POLL_INTERVAL_MS));
    }

    // Try to resume playback (best-effort) if user is still "playing".
    if (!isShuttingDown && audioRef.current && isPlayingRef.current) {
      audioRef.current.currentTime = resumeTime;
      audioRef.current.play().catch(() => {});
    }
  };

  // Start status polling for pending/processing content.
  // Also keeps polling when 'completed' to detect isSingleFile and trigger
  // the clean switch before HLS.js encounters stale manifests.
  const startStatusPolling = () => {
    if (statusPollInterval || isShuttingDown) return;
    statusPollInterval = setInterval(() => {
      if (isShuttingDown) {
        if (statusPollInterval) {
          clearInterval(statusPollInterval);
          statusPollInterval = null;
        }
        return;
      }
      if (
        currentStatus === 'pending' ||
        currentStatus === 'processing' ||
        currentStatus === 'completed'
      ) {
        checkBuffer();
      } else if (statusPollInterval) {
        clearInterval(statusPollInterval);
        statusPollInterval = null;
      }
    }, STATUS_POLL_INTERVAL_MS);
  };

  // Check buffer on timeupdate if playing and buffer is low
  const handleTimeUpdateForBuffer = () => {
    if (isShuttingDown || !isPlayingRef.current || !audioRef.current) return;

    const bufferAhead = secondsGenerated - audioRef.current.currentTime;
    if (bufferAhead < BUFFER_THRESHOLD_SECONDS) {
      checkBuffer();
    }
  };

  // HLS-specific event handlers that wrap the shared ones
  const hlsOnEnded = () => {
    if (isShuttingDown) return;

    const isLive = hls?.levels[0]?.details?.live ?? false;

    if (isLive && hls) {
      hls.startLoad();
      if (audioRef.current && isPlayingRef.current) {
        audioRef.current.play().catch(() => {});
      }
      return;
    }

    // For EVENT playlists, Hls.js often reports live=false even though more segments
    // will appear. When that happens, attempt a recovery loop before declaring ended.
    if (currentStatus === 'pending' || currentStatus === 'processing') {
      recoverFromEndIfGenerating().catch(() => {});
      return;
    }

    handlers.onEnded();
  };

  // Initialize HLS.js if supported
  if (Hls.isSupported()) {
    hls = new Hls({
      xhrSetup: (xhr) => {
        xhr.withCredentials = true;
      },
      startPosition: Math.max(0, startPosition),
      // Increased manifest retry for mobile/cellular reliability
      manifestLoadingMaxRetry: 6,
    });

    // Error retry tracking for fatal errors
    let networkErrorRetries = 0;
    let mediaErrorRetries = 0;
    const MAX_FATAL_RETRIES = 3;
    const RETRY_DELAY_MS = 2000;

    // Track when first media is ready
    let firstMediaReady = false;
    const MIN_BUFFER_BEFORE_PLAY = 5;

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      networkErrorRetries = 0;
      mediaErrorRetries = 0;
    });

    hls.on(Hls.Events.ERROR, (_event, data) => {
      // Ignore errors during shutdown (switching to single-file mode)
      if (isShuttingDown) return;

      // Check for 410 Gone response - content completed, switch to single-file mode
      if (
        data.response?.code === 410 ||
        (data.networkDetails as XMLHttpRequest)?.status === 410
      ) {
        console.log(
          '[Audio] Content completed (410), switching to single-file mode'
        );
        stopHlsImmediately();
        if (callbacksRef.current.onSwitchToSingleFile) {
          callbacksRef.current.onSwitchToSingleFile();
        }
        return;
      }

      // Log non-fatal errors for debugging (HLS.js handles these internally)
      if (!data.fatal) {
        console.warn('[Audio] HLS non-fatal error:', data.details);
        return;
      }

      console.error('[Audio] HLS fatal error:', data);

      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          networkErrorRetries++;
          console.error(
            `[Audio] Fatal network error (${networkErrorRetries}/${MAX_FATAL_RETRIES})`
          );
          if (networkErrorRetries <= MAX_FATAL_RETRIES) {
            setTimeout(() => {
              if (!isShuttingDown) {
                hls?.startLoad();
              }
            }, RETRY_DELAY_MS * networkErrorRetries);
          } else {
            setIsLoading(false);
            setError('Network error - please check your connection');
            trackError('HLS network error (fatal)', contentId);
            callbacksRef.current.onError('Network error');
            hls?.destroy();
            hls = null;
          }
          break;
        case Hls.ErrorTypes.MEDIA_ERROR:
          mediaErrorRetries++;
          console.error(
            `[Audio] Fatal media error (${mediaErrorRetries}/${MAX_FATAL_RETRIES})`
          );
          if (mediaErrorRetries <= MAX_FATAL_RETRIES) {
            setTimeout(() => {
              if (!isShuttingDown) {
                hls?.recoverMediaError();
              }
            }, RETRY_DELAY_MS * mediaErrorRetries);
          } else {
            setIsLoading(false);
            setError('Media error - audio format issue');
            trackError('HLS media error (fatal)', contentId);
            callbacksRef.current.onError('Media error');
            hls?.destroy();
            hls = null;
          }
          break;
        default:
          setIsLoading(false);
          setError('Failed to load audio');
          trackError('HLS fatal error (unknown)', contentId);
          callbacksRef.current.onError('Failed to load audio');
          hls?.destroy();
          hls = null;
          break;
      }
    });

    hls.on(Hls.Events.FRAG_BUFFERED, () => {
      if (!firstMediaReady && audioRef.current) {
        const buffered = audioRef.current.buffered;
        if (buffered.length > 0) {
          const bufferedEnd = buffered.end(buffered.length - 1);
          const bufferedAmount = bufferedEnd - audioRef.current.currentTime;

          if (bufferedAmount >= MIN_BUFFER_BEFORE_PLAY) {
            firstMediaReady = true;
            setIsLoading(false);
          }
        }
      }
      networkErrorRetries = 0;
      mediaErrorRetries = 0;
    });

    hls.loadSource(sourceUrl);
    hls.attachMedia(audioRef.current);
    audioRef.current.playbackRate = playbackRate;
  } else {
    // Native HLS (iOS Safari) - HLS.js not needed
    audioRef.current.crossOrigin = 'use-credentials';
    audioRef.current.src = sourceUrl;
    audioRef.current.load();
    audioRef.current.playbackRate = playbackRate;
  }

  // Wrap timeupdate to include buffer checking
  const wrappedOnTimeUpdate = () => {
    handlers.onTimeUpdate();
    handleTimeUpdateForBuffer();
  };

  // Attach event listeners
  audioRef.current.addEventListener('canplay', handlers.onCanPlay, {
    once: true,
  });
  audioRef.current.addEventListener('ended', hlsOnEnded);
  audioRef.current.addEventListener('waiting', handlers.onWaiting);
  audioRef.current.addEventListener('playing', handlers.onPlaying);
  audioRef.current.addEventListener('timeupdate', wrappedOnTimeUpdate);

  // Start polling for status updates (for pending/processing content)
  // Do initial check to get current status
  checkBuffer();
  startStatusPolling();

  return {
    setPlaybackRate: (rate: number) => {
      if (audioRef.current) {
        audioRef.current.playbackRate = rate;
      }
      if (hls?.media) {
        hls.media.playbackRate = rate;
      }
    },

    cleanup: () => {
      // Stop status polling
      if (statusPollInterval) {
        clearInterval(statusPollInterval);
        statusPollInterval = null;
      }

      audioRef.current?.removeEventListener('canplay', handlers.onCanPlay);
      audioRef.current?.removeEventListener('ended', hlsOnEnded);
      audioRef.current?.removeEventListener('waiting', handlers.onWaiting);
      audioRef.current?.removeEventListener('playing', handlers.onPlaying);
      audioRef.current?.removeEventListener('timeupdate', wrappedOnTimeUpdate);

      if (hls) {
        hls.off(Hls.Events.MANIFEST_PARSED);
        hls.off(Hls.Events.LEVEL_UPDATED);
        hls.off(Hls.Events.ERROR);
        hls.off(Hls.Events.FRAG_BUFFERED);
        hls.destroy();
        hls = null;
      }
    },
  };
}
